import { FilterOutlined } from '@ant-design/icons';
import { Button, Drawer, message, Modal, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@/components/Box';
import Map from '@/components/SearchRequestFilter/Map';
import Regions from '@/components/SearchRequestFilter/Regions';
import Size from '@/components/SearchRequestFilter/Size';
import UsageCategories from '@/components/SearchRequestFilter/UsageCategories';
import {
  useEditUserMutation,
  useGetFilterCountiesQuery,
} from '@/generated/graphql';
import useWidthAndHeight from '@/hooks/getWidthAndHeight';
import { User } from '@/hooks/useUser';

const { Text } = Typography;

interface Props {
  userId: string;
  searchRequestSettings: User['searchRequestSettings'];
  button?: React.FunctionComponent<{ onClick: () => void }>;
}

function SearchRequestFilter({
  userId,
  searchRequestSettings: userSearchRequestSettings,
  button: FilterButton,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const [editUser, { loading }] = useEditUserMutation({
    onCompleted: () => {
      message.success(t('Saved'));
      setVisible(false);
    },
    onError: () => message.error(t('error')),
  });
  const { isMobile } = useWidthAndHeight();

  const [searchRequestSettings, setSearchRequestSettings] = useState<
    User['searchRequestSettings']
  >(
    userSearchRequestSettings
      ? userSearchRequestSettings
      : {
          regionIds: [],
          usageCategories: [],
          size: [],
          filterEmails: true,
        },
  );
  useEffect(() => {
    if (
      JSON.stringify(userSearchRequestSettings) !=
      JSON.stringify(searchRequestSettings)
    ) {
      setSearchRequestSettings(userSearchRequestSettings);
    }
  }, [userSearchRequestSettings]);

  const { data } = useGetFilterCountiesQuery({});

  const filtersActive =
    userSearchRequestSettings?.usageCategories.length > 0 ||
    userSearchRequestSettings?.regionIds.length > 0;
  const title = (
    <Box
      flex
      justifyContent={'space-between'}
      flexWrap={'wrap'}
      flexDirection={isMobile ? 'column' : 'row'}
      verticalSpacing={isMobile ? 2 : 0}
    >
      <Box>
        <Box>Filter</Box>

        <Text type={'secondary'} style={{ fontSize: '14px' }}>
          {t('filterHelp')}
        </Text>
      </Box>
    </Box>
  );

  const footer = (
    <Box flex justifyContent="flex-end" gap={1}>
      <Button onClick={() => setVisible(false)} style={{ marginLeft: 'auto' }}>
        {t('Cancel')}
      </Button>
      <Button
        type={'primary'}
        loading={loading}
        onClick={() =>
          editUser({
            variables: {
              id: userId,
              input: {
                searchRequestSettings,
              },
            },
          })
        }
      >
        {t('Save')}
      </Button>
    </Box>
  );

  const content = (
    <Box w={'100%'} flex>
      <Box
        w={isMobile ? '100%' : '47%'}
        maxH={isMobile ? undefined : 600}
        style={isMobile ? undefined : { overflowY: 'auto' }}
        mt={2}
        verticalSpacing={2}
      >
        <UsageCategories
          searchRequestSettings={searchRequestSettings}
          setSearchRequestSettings={setSearchRequestSettings}
        />
        <Size
          searchRequestSettings={searchRequestSettings}
          setSearchRequestSettings={setSearchRequestSettings}
        />
        <Regions
          searchRequestSettings={searchRequestSettings}
          setSearchRequestSettings={setSearchRequestSettings}
          regions={data?.getRegions.regions ?? []}
        />
      </Box>
      {!isMobile && (
        <Box w={'53%'}>
          <Map
            regions={
              data?.getRegions.regions.flatMap(county =>
                searchRequestSettings.regionIds.includes(county.id)
                  ? [county]
                  : county.children
                      .filter(child =>
                        searchRequestSettings.regionIds.includes(child.id),
                      )
                      .map(child => ({ ...child, children: [] })),
              ) ?? []
            }
          />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          closable={false}
          height={'100%'}
          placement="bottom"
          open={visible}
          title={title}
          footer={footer}
        >
          {content}
        </Drawer>
      ) : (
        <Modal
          centered
          open={visible}
          width={1050}
          footer={footer}
          bodyStyle={{
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 0,
          }}
          title={title}
          closable={false}
          onCancel={async () => setVisible(false)}
        >
          {content}
        </Modal>
      )}
      {!FilterButton && (
        <Tooltip
          title={t('Filters are active')}
          open={filtersActive && !visible ? undefined : false}
        >
          <Button
            className={'tour-filter'}
            onClick={() => setVisible(true)}
            icon={<FilterOutlined />}
            danger={filtersActive}
          >
            Filter
          </Button>
        </Tooltip>
      )}
      {FilterButton && <FilterButton onClick={() => setVisible(true)} />}
    </>
  );
}

export default SearchRequestFilter;
